import React, { useRef, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  Dropdown,
  Menu,
  Input,
  Space,
  Popover,
  Select,
  Tag,
} from "antd";
import { EditOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { formatDate } from "../utils/dateUtils";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import { notification } from "antd";
import SortableHeader from "./SortableHeader";
import ColumnSelectorPopover from "./ColumnSelectorPopover";
import {
  buildFullColumnOrder,
  getVisibleColumnsFromOrder,
  orderColumnsByKeys,
  pinKeyLast,
} from "../utils/columnOrderUtils";
import {
  createGetColumnDateRangeProps,
} from "../utils/dateFilterUtils";
import DueDatePill from "./DueDatePill";

const TASK_COLUMN_OPTIONS = [
  { key: "number", label: "Number" },
  { key: "title", label: "Title" },
  { key: "formatted_ball_in_court_users", label: "Ball in Court" },
  { key: "priority", label: "Priority" },
  { key: "created_at", label: "Created Date" },
  { key: "due_date", label: "Due Date" },
  { key: "status", label: "Status" },
  { key: "closed_at_date", label: "Closed Date" },
];

const DEFAULT_COLUMNS = [
  "number",
  "title",
  "formatted_ball_in_court_users",
  "priority",
  "due_date",
  "status",
];

const TasksTableView = ({
  currentUser,
  canManageTasks,
  tasks,
  total,
  loading,
  pagination,
  onTableChange,
  filters,
  setFilters,
  searchedColumns,
  setSearchedColumns,
  searchTexts,
  setSearchTexts,
  sortField,
  sortOrder,
  onSort,
  tableKey,
  visibleColumns,
  onView,
  onUpdate,
  onDelete,
  onNotify,
  onStatusChange,
  statusUpdatePending,
  notifyingTaskId,
}) => {
  const searchInput = useRef(null);

  const getColumnDateRangeProps = createGetColumnDateRangeProps(filters, setFilters);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setSearchTexts((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const next = { ...prev };
      delete next[dataIndex];
      return next;
    });
    setSearchTexts((prev) => {
      const next = { ...prev };
      delete next[dataIndex];
      return next;
    });
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm, dataIndex)}
            size="small"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    render: (text) =>
      searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchTexts[dataIndex] || ""]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const getColumnMultiSelectProps = (dataIndex, options) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8, minWidth: 200 }} onKeyDown={(e) => e.stopPropagation()}>
        <Select
          mode="multiple"
          placeholder={`Select ${dataIndex}`}
          value={selectedKeys}
          onChange={(values) => setSelectedKeys(values)}
          style={{ width: "100%", marginBottom: 8 }}
          options={options}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              confirm();
              setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys }));
            }}
          >
            OK
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm();
              setFilters((prev) => {
                const next = { ...prev };
                delete next[dataIndex];
                return next;
              });
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: filters[dataIndex] || null,
  });

  const priorityColor = { High: "red", Medium: "gold", Low: "green" };
  const statusColor = { Open: "orange", Closed: "default", Draft: "blue" };

  const columns = [
    {
      title: (
        <SortableHeader
          title="Number"
          sortField="number"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "number",
      key: "number",
      isVisible: visibleColumns.includes("number"),
      ...getColumnSearchProps("number"),
    },
    {
      title: (
        <SortableHeader
          title="Title"
          sortField="title"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "title",
      key: "title",
      isVisible: visibleColumns.includes("title"),
      ...getColumnSearchProps("title"),
    },
    {
      title: "Ball in Court",
      dataIndex: "formatted_ball_in_court_users",
      key: "formatted_ball_in_court_users",
      width: 280,
      isVisible: visibleColumns.includes("formatted_ball_in_court_users"),
      ...getColumnSearchProps("formatted_ball_in_court_users"),
    },
    {
      title: (
        <SortableHeader
          title="Priority"
          sortField="priority"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "priority",
      key: "priority",
      isVisible: visibleColumns.includes("priority"),
      ...getColumnMultiSelectProps("priority", [
        { label: "High", value: "High" },
        { label: "Medium", value: "Medium" },
        { label: "Low", value: "Low" },
      ]),
      render: (priority) => <Tag color={priorityColor[priority]}>{priority}</Tag>,
    },
    {
      title: (
        <SortableHeader
          title="Created Date"
          sortField="created_at"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "created_at",
      key: "created_at",
      isVisible: visibleColumns.includes("created_at"),
      ...getColumnDateRangeProps("created_at", "Created Date"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Due Date"
          sortField="due_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "due_date",
      key: "due_date",
      isVisible: visibleColumns.includes("due_date"),
      ...getColumnDateRangeProps("due_date", "Due Date"),
      render: (text, record) => <DueDatePill dateString={text} status={record.status} />,
    },
    {
      title: (
        <SortableHeader
          title="Status"
          sortField="status"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "status",
      key: "status",
      isVisible: visibleColumns.includes("status"),
      ...getColumnMultiSelectProps("status", [
        { label: "Open", value: "Open" },
        { label: "Closed", value: "Closed" },
        { label: "Draft", value: "Draft" },
      ]),
      render: (status, record) => {
        if (!canManageTasks) {
          return (
            <Tag color={statusColor[status]} className="m-0">
              {status}
            </Tag>
          );
        }
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              value={status}
              onChange={(value) => onStatusChange(record.id, value)}
              className="status-select-tag w-[120px]"
              loading={statusUpdatePending}
              disabled={statusUpdatePending}
              popupRender={(menu) => menu}
              suffixIcon={<span className="text-xs text-gray-400 ml-1">▼</span>}
            >
              <Select.Option value="Open">
                <Tag color="orange" className="m-0 w-full text-center block">
                  Open
                </Tag>
              </Select.Option>
              <Select.Option value="Closed">
                <Tag color="default" className="m-0 w-full text-center block">
                  Closed
                </Tag>
              </Select.Option>
              <Select.Option value="Draft">
                <Tag color="blue" className="m-0 w-full text-center block">
                  Draft
                </Tag>
              </Select.Option>
            </Select>
          </div>
        );
      },
    },
    {
      title: (
        <SortableHeader
          title="Closed Date"
          sortField="closed_at_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={onSort}
        />
      ),
      dataIndex: "closed_at_date",
      key: "closed_at_date",
      isVisible: visibleColumns.includes("closed_at_date"),
      ...getColumnDateRangeProps("closed_at_date", "Closed Date"),
      render: (text, record) =>
        record.status === "Closed" && text ? formatDate(text) : "",
    },
    {
      title: "Action",
      key: "action",
      isVisible: true,
      render: (_, record) => {
        if (!canManageTasks) {
          const ballInCourtIds = record.ball_in_court_ids || [];
          const assigneeIds = record.assignee_ids || [];
          const isInBallInCourt = ballInCourtIds.some(
            (id) => String(id) === String(currentUser?.id),
          );
          const isAssignee = assigneeIds.some((id) => String(id) === String(currentUser?.id));
          const isTaskManager = String(record.task_manager_id) === String(currentUser?.id);
          const canRespond = isInBallInCourt || isAssignee || isTaskManager;
          return (
            <Button
              onClick={() => onView(record)}
              type={canRespond ? "primary" : "default"}
              ghost={canRespond}
              style={{ width: 90 }}
            >
              {canRespond ? "Respond" : "View"}
            </Button>
          );
        }
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="view" onClick={() => onView(record)}>
                  <Button className="w-full">View</Button>
                </Menu.Item>
                <Menu.Item key="update" onClick={() => onUpdate(record)}>
                  <Button className="w-full">Update</Button>
                </Menu.Item>
                <Menu.Item
                  key="notify"
                  onClick={() => onNotify(record)}
                  disabled={notifyingTaskId === record.id}
                >
                  <Button className="w-full" loading={notifyingTaskId === record.id}>
                    Notify Users
                  </Button>
                </Menu.Item>
                <Menu.Item key="delete">
                  <Popconfirm
                    title="Are you sure you want to delete this task?"
                    onConfirm={() => onDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button className="w-full" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<EditOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Table
        columns={orderColumnsByKeys(columns, pinKeyLast(visibleColumns, "action")).filter((col) => col.isVisible)}
        dataSource={tasks}
        rowKey="id"
        key={tableKey}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
        }}
        onChange={onTableChange}
      />
  );
};

export { TASK_COLUMN_OPTIONS, DEFAULT_COLUMNS };
export const TasksTableColumnSelector = ({
  setCurrentUser,
  visibleColumns,
  onColumnsChange,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(visibleColumns);
  const [tempOrderedColumns, setTempOrderedColumns] = useState(
    buildFullColumnOrder(visibleColumns, TASK_COLUMN_OPTIONS.map((c) => c.key)),
  );

  const handleSaveColumns = async (columnsToSave) => {
    try {
      await updateUser({ task_columns: columnsToSave });
      const updated = await fetchCurrentUser();
      setCurrentUser(updated);
      onColumnsChange(columnsToSave);
      setIsPopoverOpen(false);
      notification.success({ message: "Column preferences saved" });
    } catch {
      notification.error({ message: "Failed to save column preferences" });
    }
  };

  return (
    <Popover
      content={
        <ColumnSelectorPopover
          columnOptions={TASK_COLUMN_OPTIONS}
          orderedKeys={tempOrderedColumns}
          selectedKeys={tempSelection}
          onOrderedKeysChange={setTempOrderedColumns}
          onSelectedKeysChange={setTempSelection}
          onSave={() =>
            handleSaveColumns(getVisibleColumnsFromOrder(tempOrderedColumns, tempSelection))
          }
          onCancel={() => setIsPopoverOpen(false)}
          allowReorder
        />
      }
      title="Select Columns to Display"
      trigger="click"
      open={isPopoverOpen}
      onOpenChange={(visible) => {
        setIsPopoverOpen(visible);
        if (visible) {
          setTempSelection(visibleColumns);
          setTempOrderedColumns(
            buildFullColumnOrder(visibleColumns, TASK_COLUMN_OPTIONS.map((c) => c.key)),
          );
        }
      }}
    >
      <Button icon={<SettingOutlined />}>Columns</Button>
    </Popover>
  );
};

export default TasksTableView;
